import { authGet, authGetConditional, authPost, type ConditionalResponse } from '../http_client.js';
import type {
  EntitlementDto,
  ClaimFreeOfferResponse,
  ClaimChainOfferStepResponse,
  OfferEventType,
  OfferTriggerEventRequest,
  OfferTriggerEventResponse,
  OffersResponse,
  ValidatePurchaseRequest,
  ValidatePurchaseResponse,
} from '../dto/offers.dto.js';

function offersPath( session_id?: string ): string
{
  // locked_offers=1: this build renders a spent cycle limit as a locked card
  // with a countdown, so the server may keep such offers in the catalog.
  const params = new URLSearchParams( { locked_offers: '1' } );
  if ( session_id ) params.set( 'session_id', session_id );
  return `api/player/me/offers?${params.toString()}`;
}

export async function getOffers( session_id?: string ): Promise<OffersResponse>
{
  return authGet<OffersResponse>( offersPath( session_id ) );
}

export function getOffersConditional( session_id: string | undefined, etag: string | null ): Promise<ConditionalResponse<OffersResponse>>
{
  return authGetConditional<OffersResponse>( offersPath( session_id ), etag );
}

export async function getEntitlements(): Promise<Record<string, EntitlementDto>>
{
  return authGet<Record<string, EntitlementDto>>( 'api/player/me/offers/entitlements' );
}

export async function validateOfferPurchase( offer_id: string, payload: ValidatePurchaseRequest ): Promise<ValidatePurchaseResponse>
{
  return authPost<ValidatePurchaseResponse>( `api/player/me/offers/${encodeURIComponent( offer_id )}/validate-purchase`, payload, 30_000 );
}

export async function sendOfferEvent(
  offer_id: string,
  event_type: OfferEventType,
  context?: Record<string, unknown>,
): Promise<{ ok: boolean }>
{
  return authPost<{ ok: boolean }>( `api/player/me/offers/${encodeURIComponent( offer_id )}/events`, {
    event_type,
    context,
  } );
}

export async function claimChainOfferStep( offer_id: string, step_id: string, rewarded_ad_id?: string | null, placement_id?: string ): Promise<ClaimChainOfferStepResponse>
{
  return authPost<ClaimChainOfferStepResponse>(
    `api/player/me/offers/${encodeURIComponent( offer_id )}/claim-chain-step`,
    { step_id, ...( rewarded_ad_id ? { rewarded_ad_id } : {} ), ...( placement_id ? { placement_id } : {} ) },
  );
}

export async function claimFreeOffer(
  offer_id: string,
  claim_id: string,
  trigger_event_id?: string,
  session_id?: string,
  placement_id?: string,
  rewarded?: { ad_id: string | null },
): Promise<ClaimFreeOfferResponse>
{
  return authPost<ClaimFreeOfferResponse>(
    `api/player/me/offers/${encodeURIComponent( offer_id )}/claim-free`,
    {
      claim_id,
      ...( trigger_event_id ? { trigger_event_id } : {} ),
      ...( placement_id ? { placement_id } : {} ),
      ...( session_id ? { session_id } : {} ),
      ...( rewarded ? { rewarded: true, ...( rewarded.ad_id ? { rewarded_ad_id: rewarded.ad_id } : {} ) } : {} ),
    },
  );
}

export async function reportOfferTriggerEvent(
  payload: OfferTriggerEventRequest,
): Promise<OfferTriggerEventResponse>
{
  return authPost<OfferTriggerEventResponse>( 'api/player/me/offers/trigger-events', payload );
}
